import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, List, ListItem, ListItemIcon,
  ListItemText, IconButton, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Breadcrumbs, Chip, Menu, MenuItem, Divider,
  Paper, LinearProgress, Tooltip
} from '@mui/material';
import {
  Folder, FolderOpen, CreateNewFolder, Upload, Delete, Edit, MoreVert,
  Home, PlayArrow, Description, InsertDriveFile, Visibility, Download,
  PictureAsPdf, TableChart, Article, TextSnippet, Code, Close
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import api from '../../services/api';

const TYPE_META = {
  pdf: { color: '#E85D5D', icon: PictureAsPdf, label: 'PDF' },
  docx: { color: '#5B6BE8', icon: Article, label: 'DOCX' },
  doc: { color: '#5B6BE8', icon: Article, label: 'DOC' },
  xlsx: { color: '#10B981', icon: TableChart, label: 'XLSX' },
  xls: { color: '#10B981', icon: TableChart, label: 'XLS' },
  csv: { color: '#10B981', icon: TableChart, label: 'CSV' },
  txt: { color: '#94A3B8', icon: TextSnippet, label: 'TXT' },
  md: { color: '#94A3B8', icon: TextSnippet, label: 'MD' },
};

function extOf(name = '') {
  const m = String(name).toLowerCase().match(/\.([a-z0-9]+)$/);
  return m ? m[1] : '';
}

function typeMetaOf(name) {
  return TYPE_META[extOf(name)] || { color: '#94A3B8', icon: InsertDriveFile, label: '' };
}

const OO_API_URL = '/onlyoffice/web-apps/apps/api/documents/api.js';
const OFFICE_EXTS = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp', 'csv'];

let ooApiPromise = null;
function ensureOnlyOfficeApi() {
  if (!ooApiPromise) {
    ooApiPromise = new Promise((resolve, reject) => {
      if (window.DocsAPI && window.DocsAPI.DocEditor) { resolve(); return; }
      const s = document.createElement('script');
      s.src = OO_API_URL;
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('No se pudo cargar la API de ONLYOFFICE'));
      document.head.appendChild(s);
    });
  }
  return ooApiPromise;
}

export default function DocumentosPage() {
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [files, setFiles] = useState([]);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameName, setRenameName] = useState('');
  const [processOpen, setProcessOpen] = useState(false);
  const [processPrompt, setProcessPrompt] = useState('');
  const [processLoading, setProcessLoading] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);
  const [preview, setPreview] = useState(null); // {file, url, name, ext, kind, text}
  const [editor, setEditor] = useState(null); // {file, cfg}
  const editorRef = useRef(null);
  const quickInputRef = useRef(null);

  const loadFolders = async (parentId = null) => {
    try {
      const url = parentId ? `/documentos/folders/${parentId}` : '/documentos/folders';
      const res = await api.get(url);
      if (parentId) {
        setFolders(res.data.children || []);
        setFiles(res.data.files || []);
      } else {
        setFolders(res.data.items || res.data || []);
        setFiles([]);
      }
    } catch (err) { console.error(err); }
  };

  useEffect(() => { loadFolders(); }, []);

  const navigateTo = async (folder) => {
    if (!folder) {
      setCurrentFolder(null);
      setBreadcrumbs([]);
      setSelectedFolder(null);
      loadFolders();
      return;
    }
    setCurrentFolder(folder);
    setSelectedFolder(folder);
    setBreadcrumbs([{ id: folder.id, name: folder.name }]);
    try {
      const res = await api.get(`/documentos/folders/${folder.id}`);
      setFolders(res.data.children || []);
      setFiles(res.data.files || []);
    } catch (err) { console.error(err); }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await api.post('/documentos/folders', {
        name: newFolderName,
        parent_id: currentFolder?.id || null
      });
      setNewFolderOpen(false);
      setNewFolderName('');
      loadFolders(currentFolder?.id);
    } catch (err) { console.error(err); }
  };

  const renameFolder = async () => {
    if (!selectedFolder || !renameName.trim()) return;
    try {
      await api.put(`/documentos/folders/${selectedFolder.id}`, { name: renameName });
      setRenameOpen(false);
      setRenameName('');
      loadFolders(currentFolder?.id);
    } catch (err) { console.error(err); }
  };

  const deleteFolder = async (folder) => {
    if (!confirm(`¿Eliminar carpeta "${folder.name}"?`)) return;
    try {
      await api.delete(`/documentos/folders/${folder.id}`);
      loadFolders(currentFolder?.id);
    } catch (err) { console.error(err); }
  };

  const processFolder = async () => {
    if (!selectedFolder || !processPrompt.trim()) return;
    setProcessLoading(true);
    try {
      await api.post('/documentos/ai/process', {
        folder_id: selectedFolder.id,
        prompt: processPrompt
      });
      setProcessOpen(false);
      setProcessPrompt('');
      const res = await api.get('/documentos/ai/tasks');
      setTasks(res.data || []);
    } catch (err) { console.error(err); }
    setProcessLoading(false);
  };

  const uploadFiles = useCallback(async (acceptedFiles) => {
    for (const file of acceptedFiles) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder_id', currentFolder?.id || '');
      try {
        await api.post('/documentos/files/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } catch (err) { console.error(err); }
    }
    if (currentFolder) {
      loadFolders(currentFolder.id);
    }
  }, [currentFolder]);

  const onDrop = useCallback((acceptedFiles) => {
    uploadFiles(acceptedFiles);
  }, [uploadFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  // ---------------------------------------------------------------- archivos

  const handleView = async (file) => {
    const url = file.url;
    const ext = extOf(file.original_name || file.name);
    if (ext === 'pdf' || OFFICE_EXTS.includes(ext)) {
      openEditor(file);
      return;
    }
    if (ext === 'txt' || ext === 'md') {
      let text = '';
      try {
        const res = await fetch(url);
        text = await res.text();
      } catch (e) { /* noop */ }
      setPreview({ file, url, name: file.original_name || file.name, ext, kind: 'text', text });
      return;
    }
    setPreview({ file, url, name: file.original_name || file.name, ext, kind: 'download' });
  };

  // ------------------------------------------------------------- onlyoffice

  const openEditor = async (file) => {
    try {
      const res = await api.get('/documents/editor-config', { params: { path: file.id } });
      setPreview(null);
      setEditor({ file, cfg: res.data });
    } catch (err) {
      console.error(err);
      alert('No se pudo abrir el documento en el editor en línea');
    }
  };

  useEffect(() => {
    if (!editor) return undefined;
    let cancelled = false;
    let raf1; let raf2;
    const mountTimer = setTimeout(() => {
      ensureOnlyOfficeApi()
        .then(() => {
          if (cancelled) return;
          if (editorRef.current) {
            try { editorRef.current.destroyEditor(); } catch (e) { /* noop */ }
            editorRef.current = null;
          }
          raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(() => {
              const el = document.getElementById('onlyoffice-editor');
              if (cancelled || !el || !window.DocsAPI) return;
              const cfg = editor.cfg;
              editorRef.current = new window.DocsAPI.DocEditor('onlyoffice-editor', {
          document: {
            fileType: cfg.fileType,
            key: cfg.key,
            title: cfg.title,
            url: cfg.url,
            permissions: { edit: true, download: true, print: true },
          },
          documentType: cfg.documentType,
          type: 'desktop',
          editorConfig: {
            mode: cfg.mode,
            lang: 'es',
            callbackUrl: cfg.callbackUrl,
            customization: {
              autosave: true,
              forcesave: true,
              compactHeader: true,
              toolbar: true,
              chat: false,
              zoom: 100,
            },
            user: { name: 'Usuario UNIFIX' },
          },
          height: '100%',
          width: '100%',
              });
            });
          });
        })
        .catch((e) => console.error(e));
    }, 50);
    return () => {
      cancelled = true;
      clearTimeout(mountTimer);
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [editor]);

  useEffect(() => () => {
    try { editorRef.current && editorRef.current.destroyEditor(); } catch (e) { /* noop */ }
  }, []);

  const closeEditor = () => {
    try { editorRef.current && editorRef.current.destroyEditor(); } catch (e) { /* noop */ }
    editorRef.current = null;
    setEditor(null);
    if (currentFolder) loadFolders(currentFolder.id); else loadFolders();
  };

  const handleDownload = async (file) => {
    try {
      const res = await fetch(file.url);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = file.original_name || file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);
    } catch (e) {
      window.open(file.url, '_blank', 'noopener');
    }
  };

  const handleDeleteFile = async (file) => {
    if (!confirm(`¿Eliminar el archivo "${file.original_name || file.name}"?`)) return;
    try {
      await api.delete(`/documentos/files/${file.id}`);
      if (currentFolder) loadFolders(currentFolder.id); else loadFolders();
    } catch (err) { console.error(err); }
  };

  const FileIcon = ({ name }) => {
    const meta = typeMetaOf(name);
    const Icon = meta.icon;
    return (
      <Tooltip title={meta.label || 'Archivo'}>
        <Icon sx={{ color: meta.color }} />
      </Tooltip>
    );
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {/* Panel izquierdo - árbol de carpetas */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: 'calc(100vh - 180px)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Carpetas</Typography>
                <Button startIcon={<CreateNewFolder />} size="small" onClick={() => setNewFolderOpen(true)}>
                  Nueva
                </Button>
              </Box>

              {currentFolder && (
                <Breadcrumbs sx={{ mb: 1, fontSize: 12 }}>
                  <Box component="span" sx={{ cursor: 'pointer', color: 'primary.main' }}
                    onClick={() => navigateTo(null)}>Inicio</Box>
                  {breadcrumbs.map((b) => (
                    <Box key={b.id} component="span"
                      sx={{ cursor: 'pointer', fontWeight: b.id === currentFolder?.id ? 600 : 400, color: 'primary.main' }}
                      onClick={() => navigateTo(b)}>
                      {b.name}
                    </Box>
                  ))}
                </Breadcrumbs>
              )}

              <List dense>
                {!currentFolder && (
                  <ListItem selected={!currentFolder}
                    sx={{ borderRadius: 1, cursor: 'pointer', bgcolor: '#e3f2fd' }}>
                    <ListItemIcon><Home color="primary" /></ListItemIcon>
                    <ListItemText primary="Mis Documentos" />
                  </ListItem>
                )}
                {folders.map((folder) => (
                  <ListItem key={folder.id}
                    onDoubleClick={() => navigateTo(folder)}
                    onClick={() => setSelectedFolder(folder)}
                    selected={selectedFolder?.id === folder.id}
                    sx={{ borderRadius: 1, cursor: 'pointer' }}
                    secondaryAction={
                      <IconButton size="small" onClick={(e) => {
                        e.stopPropagation();
                        setContextMenu({ anchorEl: e.currentTarget, folder });
                      }}>
                        <MoreVert fontSize="small" />
                      </IconButton>
                    }>
                    <ListItemIcon>
                      {selectedFolder?.id === folder.id ? <FolderOpen color="primary" /> : <Folder />}
                    </ListItemIcon>
                    <ListItemText primary={folder.name} primaryTypographyProps={{ noWrap: true, fontSize: 13 }} />
                  </ListItem>
                ))}
                {folders.length === 0 && (
                  <Typography variant="body2" sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>
                    Sin carpetas. Cree una nueva.
                  </Typography>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Panel derecho - contenidos */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: 'calc(100vh - 180px)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {currentFolder ? currentFolder.name : 'Mis Documentos'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <input ref={quickInputRef} type="file" multiple style={{ display: 'none' }}
                    onChange={(e) => {
                      if (e.target.files?.length) uploadFiles(Array.from(e.target.files));
                      e.target.value = '';
                    }} />
                  <Button variant="outlined" startIcon={<Upload />}
                    onClick={() => quickInputRef.current?.click()}>
                    ⬆ Subir Archivo
                  </Button>
                  <Button variant="contained" startIcon={<PlayArrow />} disabled={!selectedFolder}
                    onClick={() => setProcessOpen(true)}>
                    ▶ Procesar
                  </Button>
                </Box>
              </Box>

              {/* Zona drag & drop */}
              <Box {...getRootProps()} sx={{
                border: '2px dashed', borderColor: isDragActive ? '#00A3FF' : '#ccc',
                borderRadius: 2, p: 3, textAlign: 'center', mb: 2,
                bgcolor: isDragActive ? '#e3f2fd' : 'transparent',
                cursor: 'pointer', transition: 'all 0.2s'
              }}>
                <input {...getInputProps()} />
                <Upload sx={{ fontSize: 40, color: '#00A3FF', mb: 1 }} />
                <Typography variant="body2">
                  {isDragActive ? 'Suelte los archivos aquí...' : 'Arrastre archivos aquí o haga clic para seleccionar'}
                </Typography>
                {currentFolder && (
                  <Typography variant="caption" color="text.secondary">
                    Se guardarán en: {currentFolder.name}
                  </Typography>
                )}
              </Box>

              {/* Lista de archivos */}
              <Divider sx={{ mb: 1 }} />
              <List dense>
                {files.map((file) => {
                  const displayName = file.original_name || file.name;
                  return (
                    <ListItem key={file.id}
                      onClick={() => handleView(file)}
                      secondaryAction={
                        <Box sx={{ display: 'flex', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Ver / Previsualizar">
                            <IconButton size="small" sx={{ color: '#0EA5E9' }}
                              onClick={() => handleView(file)}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Descargar">
                            <IconButton size="small" sx={{ color: '#16A34A' }}
                              onClick={() => handleDownload(file)}>
                              <Download fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton size="small" color="error"
                              onClick={() => handleDeleteFile(file)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                      sx={{
                        borderRadius: 1,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        '&:hover': { bgcolor: 'rgba(30,41,59,0.07)', transform: 'translateX(2px)' },
                        pr: 14,
                      }}>
                      <ListItemIcon><FileIcon name={displayName} /></ListItemIcon>
                      <ListItemText
                        primary={
                          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{displayName}</span>
                            {typeMetaOf(displayName).label && (
                              <Chip size="small" variant="outlined"
                                label={typeMetaOf(displayName).label}
                                sx={{
                                  height: 18, fontSize: 9, fontWeight: 700,
                                  color: typeMetaOf(displayName).color,
                                  borderColor: typeMetaOf(displayName).color,
                                }} />
                            )}
                          </Box>
                        }
                        secondary={`${(file.file_size / 1024).toFixed(1)} KB`}
                      />
                    </ListItem>
                  );
                })}
                {files.length === 0 && (
                  <Typography variant="body2" sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>
                    Sin archivos. Arrastre archivos aquí.
                  </Typography>
                )}
              </List>

              {/* Tareas IA */}
              {tasks.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 1 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Tareas IA</Typography>
                  {tasks.map(task => (
                    <Chip key={task.id} label={`${task.status}: ${task.prompt?.substring(0, 30)}`}
                      color={task.status === 'completed' ? 'success' : task.status === 'failed' ? 'error' : 'warning'}
                      sx={{ mr: 1, mb: 1 }} />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Modal editor ONLYOFFICE (docx/xlsx/pptx/pdf) */}
      <Dialog
        open={Boolean(editor)}
        onClose={closeEditor}
        maxWidth={false}
        PaperProps={{
          sx: { width: '90vw', height: '85vh', maxWidth: '90vw', maxHeight: '85vh' },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
          {editor && <FileIcon name={editor.file.original_name || editor.file.name} />}
          <Box component="span" sx={{ fontWeight: 600 }}>
            {editor?.cfg?.title}
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          {editor?.cfg?.mode === 'edit' && (
            <Chip size="small" color="success" label="Edición en línea" sx={{ mr: 1 }} />
          )}
          <Button startIcon={<Download />} size="small"
            onClick={() => editor && handleDownload(editor.file)}>
            Descargar
          </Button>
          <IconButton size="small" onClick={closeEditor}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers={false} sx={{ p: 0, overflow: 'hidden', bgcolor: '#F5F5F5' }}>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: 'calc(85vh - 64px)',
              minHeight: 600,
              overflow: 'hidden',
            }}
          >
            <div
              id="onlyoffice-editor"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Modal previsualización (solo texto plano / fallback) */}
      <Dialog open={Boolean(preview)} onClose={() => setPreview(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {preview && <FileIcon name={preview.name} />}
          {preview?.name}
        </DialogTitle>
        <DialogContent>
          {preview?.kind === 'text' ? (
            <Paper variant="outlined" sx={{ p: 2, maxHeight: 380, overflow: 'auto' }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.82rem' }}>
                {(preview.text || '').substring(0, 20000)}
              </pre>
            </Paper>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Vista previa no disponible para este formato
              {preview?.ext ? ` (.${preview.ext})` : ''}. Puedes descargarlo para
              abrirlo con la aplicación correspondiente.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)}>Cerrar</Button>
          <Button variant="contained" startIcon={<Download />}
            onClick={() => preview && handleDownload(preview.file)}>
            Descargar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Menú contextual carpetas */}
      <Menu anchorEl={contextMenu?.anchorEl} open={Boolean(contextMenu)} onClose={() => setContextMenu(null)}>
        <MenuItem onClick={() => { setRenameName(contextMenu?.folder?.name || ''); setRenameOpen(true); setContextMenu(null); }}>
          <ListItemIcon><Edit fontSize="small" /></ListItemIcon> Renombrar
        </MenuItem>
        <MenuItem onClick={() => { navigateTo(contextMenu?.folder); setContextMenu(null); }}>
          <ListItemIcon><FolderOpen fontSize="small" /></ListItemIcon> Abrir
        </MenuItem>
        <MenuItem onClick={() => { deleteFolder(contextMenu?.folder); setContextMenu(null); }}
          sx={{ color: 'error.main' }}>
          <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon> Eliminar
        </MenuItem>
      </Menu>

      {/* Diálogo nueva carpeta */}
      <Dialog open={newFolderOpen} onClose={() => setNewFolderOpen(false)}>
        <DialogTitle>Nueva Carpeta</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="Nombre de la carpeta" value={newFolderName}
            onChange={e => setNewFolderName(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewFolderOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={createFolder}>Crear</Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo renombrar */}
      <Dialog open={renameOpen} onClose={() => setRenameOpen(false)}>
        <DialogTitle>Renombrar Carpeta</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="Nuevo nombre" value={renameName}
            onChange={e => setRenameName(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={renameFolder}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo procesar */}
      <Dialog open={processOpen} onClose={() => setProcessOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Procesar Carpeta con IA</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Carpeta: <strong>{selectedFolder?.name}</strong>
          </Typography>
          <TextField fullWidth multiline rows={4} label="Instrucciones para el agente IA"
            value={processPrompt} onChange={e => setProcessPrompt(e.target.value)} />
          {processLoading && <LinearProgress sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProcessOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={processFolder} disabled={processLoading || !processPrompt.trim()}>
            {processLoading ? 'Procesando...' : 'Procesar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
